import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react/lib/Spinner";

import Backdrop from "../../components/backdrop/Backdrop";
import { HeaderAddToIci, Listing, SavedSearch, ReplaceExisting, Banner } from "./flows/drag-and-drop/index";

import ConfigService from "../../shared/services/configService";
import { TEXT } from "../../shared/constants/texts";
import { logError } from "../../shared/services/errorLogging";
import { transformAttributeData, updateAttributeData } from "./utilFunctions";
import {
  resetIndexAddToIci,
  startFetchingAttributes,
  startFetchingMsGraphTokens,
  stopFetchingMsGraphTokens,
} from "./redux/slice";
import { attributeApiCall, msGraphTokenApiCall, redirectToLogin } from "./redux/apiCalls";
import { getValidatedEmail, isValidAuthUrl, isValidRedirectUrl } from "./utils";

const configService = new ConfigService();

const AddToIci: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.addToIciSlice);
  const { isFetchingSavedSearchData, isReplaceExistingPopupOpen, isFetchingMsGraphTokens, isAdvancePageEnabled } =
    state;
  const [authorized, setAuthorized] = React.useState(false);
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    const isMsGraphEnabled = configService.getMsGraphFlag().toString();
    const tenantId = configService.getTenantId().toString();
    const clientId = configService.getClientId().toString();
    if (isMsGraphEnabled === "true" && clientId && tenantId) {
      dispatch(startFetchingMsGraphTokens());
      if (localStorage.getItem(TEXT.MSG_ACCESS_TOKEN) && localStorage.getItem(TEXT.MSG_REFRESH_TOKEN)) {
        setAuthorized(true);
        dispatch(stopFetchingMsGraphTokens());
      } else {
        const windowLocationSearch = window.location.search;
        let code = "";
        let state = "";
        let sessionState = "";
        if (windowLocationSearch.indexOf("?code") >= 0) {
          code = decodeURIComponent(
            windowLocationSearch
              .substring(windowLocationSearch.indexOf("=") + 1, windowLocationSearch.indexOf("&"))
              .toString()
          );
        }
        if (windowLocationSearch.indexOf("state") >= 0) {
          state = decodeURIComponent(
            windowLocationSearch
              .substring(windowLocationSearch.indexOf("state") + 6, windowLocationSearch.indexOf("&session_state"))
              .toString()
          );
        }
        if (windowLocationSearch.indexOf("session_state") >= 0) {
          sessionState = decodeURIComponent(
            windowLocationSearch.substring(windowLocationSearch.lastIndexOf("=") + 1).toString()
          );
        }
        if (code && state && sessionState) {
          getMsGraphTokens(code, state, sessionState);
        } else {
          const msGraphAuthUrl = configService.getMsGraphAuthUrl().toString();
          const msGraphRedirectUrl = configService.getRedirectUri().toString();
          const random5digitNum = Math.floor(Math.random() * 90000) + 10000;
          const userEmailId = localStorage.getItem(TEXT.USER_EMAIL_ID) || "";
          const scope = "offline_access user.read mail.readwrite";
          const validEmail = getValidatedEmail(userEmailId);
          const isRedirectUrlValid = isValidRedirectUrl(msGraphRedirectUrl);
          const isMsGraphUrlValid = isValidAuthUrl(msGraphAuthUrl);
          if (isRedirectUrlValid && isMsGraphUrlValid) {
            const loginHintParam = validEmail ? `&login_hint=${encodeURIComponent(userEmailId)}` : "";
            const msUrl = `${msGraphAuthUrl}/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&response_mode=query&scope=${encodeURIComponent(
              scope
            )}&state=${random5digitNum}${loginHintParam}&redirect_uri=${encodeURIComponent(msGraphRedirectUrl)}`;
            window.location.href = msUrl;
          } else {
            console.log("Invalid MS Graph URL");
            logError({
              dispatch: dispatch,
              err: "Invalid MS Graph URL: Either the redirect URL or the MS Graph auth URL is invalid.",
              callerInfo: {
                callerFunction: "ClientApp|src|taskpane|containers|add-to-ici|index",
                callerApi: "",
              },
            });
          }
        }
      }
    }
  }, []);

  React.useEffect(() => {
    localStorage.setItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY, JSON.stringify(errorValidationMsg));
    getAttributes();
    return () => {
      dispatch(resetIndexAddToIci());
    };
  }, []);

  const getMsGraphTokens = async (code, state, sessionState) => {
    const msGraphRedirectUrl = configService.getRedirectUri().toString();
    const msGraphResponse = await msGraphTokenApiCall({
      dispatch: dispatch,
      history: history,
      requestData: {
        code: code,
        state: state,
        sessionState: sessionState,
        redirectUrl: encodeURIComponent(msGraphRedirectUrl),
      },
    });
    const { response, error } = msGraphResponse;
    if (response) {
      const accessToken = response.AccessToken || "";
      const refreshToken = response.RefreshToken || "";
      localStorage.setItem(TEXT.MSG_ACCESS_TOKEN, accessToken);
      localStorage.setItem(TEXT.MSG_REFRESH_TOKEN, refreshToken);
      setAuthorized(true);
    }
    if (error) {
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|add-to-ici|index - getMsGraphTokens",
          callerApi: "api/user/GetUserMsGraphToken?code=&state=&session_state=&redirectUrl=",
        },
      });
      localStorage.setItem(TEXT.MSG_ACCESS_TOKEN, "");
      localStorage.setItem(TEXT.MSG_REFRESH_TOKEN, "");
      redirectToLogin(history);
    }
    dispatch(stopFetchingMsGraphTokens());
  };

  const getAttributes = async () => {
    dispatch(startFetchingAttributes());
    const attributeResponse = await attributeApiCall({
      ...apiCallParams,
      retryFunctionCall: () => getAttributes(),
    });
    const { response, error } = attributeResponse;
    if (response) {
      const attributeData = transformAttributeData(response);
      updateAttributeData(attributeData);
    }
    if (error) {
      updateAttributeData([]);
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction: "ClientApp|src|taskpane|containers|add-to-ici|index - getAttributes",
          callerApi: "api/instance/FindProjectionsKeyValuePair",
        },
      });
    }
  };

  const isMsGraphEnabled = configService.getMsGraphFlag().toString();

  console.log("state in add to ici (index):", state);

  return isFetchingMsGraphTokens && isMsGraphEnabled === "true" ? (
    <div className="loading-ms-graph-add-to-ici">
      <Spinner size={3} label={TEXT.LOADING} />
    </div>
  ) : authorized || isMsGraphEnabled === "false" ? (
    <React.Fragment>
      <div className="add-to-ici" style={isAdvancePageEnabled ? { marginLeft: "1rem" } : {}}>
        <Banner />
        <HeaderAddToIci />
        <SavedSearch />
        {isFetchingSavedSearchData ? (
          <div className="saved-search-loading-container-add-to-ici">
            <Spinner size={3} label={TEXT.LOADING_SAVED_SEARCHES} />
          </div>
        ) : (
          <Listing />
        )}
      </div>
      {isReplaceExistingPopupOpen && isMsGraphEnabled === "true" ? (
        <React.Fragment>
          <Backdrop show={isReplaceExistingPopupOpen} />
          <ReplaceExisting />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  ) : null;
};

export default AddToIci;
